import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useInitiateManualPaymentMutation } from "../../store/api/manualPaymentSlice";
import { useGetAllUsersQuery } from "../../store/api/userSlice";
import "./MakePayment.scss";

// Which roles can this payer pay to?
const RECIPIENT_ROLES = {
  super_admin: ["school", "vendor"],
  school: ["vendor", "super_admin"],
  vendor: ["school", "super_admin"],
};

const CURRENCIES = [
  { value: "CAD", label: "CAD — Canadian Dollar" },
  { value: "USD", label: "USD — US Dollar" },
];

const MakePayment = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const loggedRole = user?.role;

  const [recipientRole, setRecipientRole] = useState("");
  const [recipientUser, setRecipientUser] = useState(null);
  const [amountDollars, setAmountDollars] = useState("");
  const [currency, setCurrency] = useState({
    value: "CAD",
    label: "CAD — Canadian Dollar",
  });
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const allowedRecipientRoles = RECIPIENT_ROLES[loggedRole] || [];

  // Fetch users based on selected recipient role
  const { data: usersData, isFetching: fetchingUsers } = useGetAllUsersQuery(
    { role: recipientRole, limit: 100, active: "yes" },
    { skip: !recipientRole },
  );

  const userOptions =
    usersData?.data?.users?.map((u) => ({
      value: u.id,
      label: `${u.full_name} — ${u.email}`,
      type: u.role,
    })) || [];

  const [initiatePayment] = useInitiateManualPaymentMutation();

  // Reset recipient when role changes
  useEffect(() => {
    setRecipientUser(null);
  }, [recipientRole]);

  const validate = () => {
    const e = {};
    if (!recipientRole) e.recipientRole = t("pleaseSelectRecipientType");
    if (!recipientUser) e.recipientUser = t("pleaseSelectRecipient");
    if (!amountDollars || isNaN(amountDollars) || Number(amountDollars) <= 0)
      e.amount = t("pleaseEnterValidAmount");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const amountCents = Math.round(Number(amountDollars) * 100);

    setLoading(true);
    try {
      const res = await initiatePayment({
        recipient_id: recipientUser.value,
        recipient_type: recipientUser.type,
        amount_cents: amountCents,
        currency: currency.value,
        description: description.trim() || undefined,
      }).unwrap();

      // Save manual_payment_id for the complete page
      localStorage.setItem("manual_payment_id", res.data.manual_payment_id);
      localStorage.setItem("manual_payment_txn", res.data.transaction_number);

      // Open Omni iframe in new tab (same as existing subscription flow)
      window.open(res.data.iframeUrl, "_blank");

      toast.success("Payment session opened. Complete payment in the new tab.");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to initiate payment.");
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = (role) => {
    if (role === "super_admin") return "Admin";
    if (role === "school") return "School";
    if (role === "vendor") return "Vendor";
    return role;
  };

  return (
    <div className="">
      {/* Page Header */}
      <div className="mp-header">
        <div className="mp-header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        </div>
        <div>
          <h2 className="mp-title">
            {t("make")} {t("payment")}
          </h2>
          <p className="mp-subtitle">{t("processCreditCardPayment")}</p>
        </div>
      </div>

      <div className="mp-card-grid">
        {/* Payment Form Card */}
        <div className="mp-card mp-form-card">
          <form onSubmit={handleSubmit}>
            {/* Step 1 — Recipient */}
            <div className="mp-section">
              <div className="mp-section-label">
                <span className="mp-step">1</span>
                {t("select")} {t("recipient")}
              </div>

              <div className="mp-field">
                <label>
                  {t("recipient")} {t("type")}
                </label>
                <div className="mp-role-buttons">
                  {allowedRecipientRoles.map((role) => (
                    <button
                      key={role}
                      type="button"
                      className={`mp-role-btn ${recipientRole === role ? "active" : ""}`}
                      onClick={() => setRecipientRole(role)}
                    >
                      {roleLabel(role)}
                    </button>
                  ))}
                </div>
                {errors.recipientRole && (
                  <span className="mp-error">{errors.recipientRole}</span>
                )}
              </div>

              {recipientRole && (
                <div className="mp-field">
                  <label>
                    {t("select")} {roleLabel(recipientRole)}
                  </label>
                  <Select
                    options={userOptions}
                    value={recipientUser}
                    onChange={setRecipientUser}
                    isLoading={fetchingUsers}
                    placeholder={`${t("search")} ${roleLabel(recipientRole)}...`}
                    isClearable
                    classNamePrefix="mp-select"
                    noOptionsMessage={() =>
                      `${t("no")} ${roleLabel(recipientRole)} ${t("found")}`
                    }
                  />
                  {errors.recipientUser && (
                    <span className="mp-error">{errors.recipientUser}</span>
                  )}
                </div>
              )}
            </div>

            {/* Step 2 — Amount */}
            <div className="mp-section">
              <div className="mp-section-label">
                <span className="mp-step">2</span>
                {t("payment")} {t("amount")}
              </div>

              <div className="mp-field-row">
                <div className="mp-field mp-field-grow">
                  <label>{t("amount")}</label>
                  <div className="mp-amount-input">
                    <span className="mp-currency-symbol">
                      {currency.value === "CAD" ? "CA$" : "$"}
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amountDollars}
                      onChange={(e) => setAmountDollars(e.target.value)}
                      placeholder="0.00"
                      className={errors.amount ? "input-error" : ""}
                    />
                  </div>
                  {errors.amount && (
                    <span className="mp-error">{errors.amount}</span>
                  )}
                </div>

                <div className="mp-field mp-field-currency">
                  <label>{t("currency")}</label>
                  <Select
                    options={CURRENCIES}
                    value={currency}
                    onChange={setCurrency}
                    // isLoading={fetchingUsers}
                    placeholder={`${t("search")}...`}
                    classNamePrefix="mp-select"
                    noOptionsMessage={() => `No result found`}
                  />
                </div>
              </div>
            </div>

            {/* Step 3 — Description */}
            <div className="mp-section">
              <div className="mp-section-label">
                <span className="mp-step">3</span>
                {t("description")}{" "}
                <span className="mp-optional">({t("optional")})</span>
              </div>

              <div className="mp-field">
                <textarea
                  rows={3}
                  maxLength={500}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("placeholderExample")}
                />
                <span className="mp-char-count">{description.length}/500</span>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary mp-pay-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  {t("creating")} {t("session")}...
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  {t("pay")} {t("now")}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Summary Card */}
        <div className="mp-card mp-summary-card">
          <h4 className="mp-summary-title">
            {t("payment")} {t("summary")}
          </h4>

          <div className="mp-summary-row">
            <span>{t("payer")}</span>
            <span className="mp-summary-val">
              {user?.full_name || "—"}
              <br />
              <small className="text-muted">{roleLabel(loggedRole)}</small>
            </span>
          </div>

          <div className="mp-summary-divider" />

          <div className="mp-summary-row">
            <span>{t("recipient")}</span>
            <span className="mp-summary-val">
              {recipientUser ? (
                <>
                  {recipientUser.label.split(" — ")[0]}
                  <br />
                  <small className="text-muted">
                    {roleLabel(recipientRole)}
                  </small>
                </>
              ) : (
                <span className="text-muted">
                  {t("not")} {t("selected")}
                </span>
              )}
            </span>
          </div>

          <div className="mp-summary-divider" />

          <div className="mp-summary-row">
            <span>{t("amount")}</span>
            <span className="mp-summary-amount">
              {amountDollars
                ? `${currency.value === "CAD" ? "CA$" : "$"}${Number(amountDollars).toFixed(2)} ${currency.value}`
                : "—"}
            </span>
          </div>

          {description && (
            <>
              <div className="mp-summary-divider" />
              <div className="mp-summary-row mp-summary-desc">
                <span>{t("note")}</span>
                <span className="mp-summary-val">{description}</span>
              </div>
            </>
          )}

          <div className="mp-info-box">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>
              {t("paymentIsProcessed")} <strong>{t("your")}</strong>{" "}
              {t("linkedMerchantAccount")}. {t("redirectedSecurePage")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MakePayment;
